import { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { KoobInputCountries } from '@koob/ui';
import { useFormikContext } from 'formik';
import { FormField, FormLabel } from 'ui/forms';
import { keys, pickBy } from 'lodash';
import { useApp } from 'hooks';
import { useAsync } from 'react-async';
import { getAllCountriesByDmcId } from 'api/node/countries';
import OrganizationCountryBookingReceptionEmailsModal from '../OrganizationCountryBookingReceptionEmailsModal';

const Sublabel = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing()};
  color: ${({ theme }) => theme.textLight};
`;

const toBooleanMap = (ids) => Object.fromEntries(ids.map((id) => [id, true]));

// Formik keeps `{ [name]: { [regionId]: bool }, countries: { [countryId]: bool } }`.
// A country flag is true when all its regions are picked (or the country has
// no region and is picked itself).
const RegionsSelectorField = ({ label, sublabel, name }) => {
  const { t } = useTranslation('user');
  const { currentUserRole } = useApp();
  const { values, setFieldValue } = useFormikContext();
  const [emailsCountryId, setEmailsCountryId] = useState(null);

  const { data: countriesData, isLoading } = useAsync({
    promiseFn: getAllCountriesByDmcId,
  });

  const countries = (countriesData?.data ?? []).map((country) => ({
    value: String(country.id),
    label: country.title,
    code: country.alpha2,
    regions: (country.regions ?? []).map((region) => ({
      value: String(region.id),
      label: region.title,
    })),
  }));

  return (
    <FormField>
      {Boolean(label) && <FormLabel>{label}</FormLabel>}
      {Boolean(sublabel) && <Sublabel>{sublabel}</Sublabel>}
      <KoobInputCountries
        countries={countries}
        value={{
          countries: keys(pickBy(values?.countries)),
          regions: keys(pickBy(values?.[name])),
        }}
        onValueChange={({ countries, regions }) => {
          setFieldValue(name, toBooleanMap(regions));
          setFieldValue('countries', toBooleanMap(countries));
        }}
        placeholder={t('searchDestination')}
        childrenLabel={(count) => t('region', { count })}
        emptySearchTitle={t('noCountryFound')}
        loading={isLoading}
        renderCountryExtra={
          currentUserRole === 'koob_admin'
            ? (country) => (
                <button
                  type="button"
                  onClick={() => setEmailsCountryId(country.value)}
                  className="cursor-pointer border-none bg-transparent p-0 text-gray-400 hover:text-gray-600"
                  aria-label={t(
                    'organization:form.bookingReceptionEmailModal.title',
                  )}
                >
                  <i className="fa-regular fa-envelope" />
                </button>
              )
            : undefined
        }
        data-testid="destinations"
      />
      {currentUserRole === 'koob_admin' && (
        <OrganizationCountryBookingReceptionEmailsModal
          isOpen={emailsCountryId !== null}
          setOpen={(open) => !open && setEmailsCountryId(null)}
          countryId={emailsCountryId}
        />
      )}
    </FormField>
  );
};

export default RegionsSelectorField;
